import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import moment from "moment";
import {
  RiCheckboxCircleLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiPrinterLine,
  RiDownloadLine,
  RiTimeLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiArrowLeftRightLine,
  RiArrowLeftLine,
  RiErrorWarningLine,
} from "react-icons/ri";

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengerData, setPassengerData] = useState([]);
  const [seatData, setSeatData] = useState([]);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(
        `${config.GETBUSBOOKINGBYID}/${bookingId}`
      );

      if (response) {
        setBooking(response);

        // Parse passenger details from JSON string
        if (response.passengerDetails) {
          try {
            const parsedPassengers = JSON.parse(response.passengerDetails);
            setPassengerData(
              Array.isArray(parsedPassengers)
                ? parsedPassengers
                : [parsedPassengers]
            );
          } catch (e) {
            console.error("Error parsing passenger details:", e);
            setPassengerData([]);
          }
        }

        // Parse seat details from JSON string
        if (response.seatDetails) {
          try {
            const parsedSeats = JSON.parse(response.seatDetails);
            setSeatData(
              Array.isArray(parsedSeats) ? parsedSeats : [parsedSeats]
            );
          } catch (e) {
            console.error("Error parsing seat details:", e);
            setSeatData([]);
          }
        }
      } else {
        setError("Booking details not found");
      }
    } catch (err) {
      setError("Failed to fetch booking details. Please try again.");
      console.error("Fetch booking error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/bus/bookinglist");
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const formatDateTime = (dateString) => {
    return moment(dateString).format("DD MMM YYYY, hh:mm A");
  };

  const formatTime = (dateString) => {
    return moment(dateString).format("hh:mm A");
  };

  const handlePrint = () => {
    const printContent = document.getElementById("booking-details").innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Booking Details - ${booking?.bookingNo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .card { border: 1px solid #ddd; border-radius: 8px; margin-bottom: 20px; }
          .card-header { background: #f8f9fa; padding: 15px; border-bottom: 1px solid #ddd; }
          .card-body { padding: 20px; }
          .text-primary { color: #4361ee; }
          .text-success { color: #0ca678; }
          .text-warning { color: #f59f00; }
          .fare-summary { background: #f8f9fa; padding: 15px; border-radius: 8px; }
          .fare-line { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .fare-total { border-top: 2px solid #4361ee; padding-top: 10px; margin-top: 10px; font-weight: bold; }
          .table { width: 100%; border-collapse: collapse; }
          .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .table th { background: #f8f9fa; }
          .badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
          .bg-success { background: #0ca678; color: white; }
          .bg-danger { background: #f03e3e; color: white; }
          .bg-warning { background: #f59f00; color: white; }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="print-header" style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px;">
          <h1 style="color: #2c3e50; margin-bottom: 10px;">Booking Confirmation</h1>
          <p style="color: #7f8c8d; margin: 5px 0;">Booking No: ${
            booking?.bookingNo
          }</p>
          <p style="color: #7f8c8d; margin: 5px 0;">Booking Date: ${formatDateTime(
            booking?.bookingDate
          )}</p>
          <p style="color: #7f8c8d; margin: 5px 0;">Status: ${
            booking?.bookingStatus
          } | Payment: ${booking?.paymentStatus}</p>
        </div>
        ${printContent}
        <div class="print-footer" style="text-align: center; margin-top: 40px; font-size: 12px; color: #7f8c8d; border-top: 1px solid #ddd; padding-top: 20px;">
          <p>This is a computer-generated document. No signature required.</p>
          <p>Convenience fee of ₹100 per seat is included in the total amount</p>
          <p>For any queries, contact support@travelcompany.com or call 1800-XXX-XXXX</p>
        </div>
      </body>
      </html>
    `;

    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleDownload = () => {
    handlePrint();
  };

  if (loading) {
    return (
      <div id="layout-wrapper">
        <Header User={user} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5>Loading Booking Details...</h5>
                      <p className="text-muted">
                        Please wait while we fetch your booking information
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div id="layout-wrapper">
        <Header User={user} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div className="mb-4">
                        <RiCheckboxCircleLine className="text-danger fs-1" />
                      </div>
                      <h4 className="text-danger mb-3">
                        {error || "Booking not found"}
                      </h4>
                      <p className="text-muted mb-4">
                        The booking you're looking for doesn't exist or you
                        don't have permission to view it.
                      </p>
                      <button onClick={handleBack} className="btn btn-primary">
                        <RiArrowLeftLine className="me-2" />
                        Back to Bookings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Action Buttons - Top Right */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <button
                      onClick={handleBack}
                      className="btn btn-outline-primary d-flex align-items-center gap-2"
                    >
                      <RiArrowLeftLine /> Back to Bookings
                    </button>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-secondary d-flex align-items-center gap-2"
                      onClick={handlePrint}
                    >
                      <RiPrinterLine /> Print PDF
                    </button>
                    <button
                      className="btn btn-success d-flex align-items-center gap-2"
                      onClick={handleDownload}
                    >
                      <RiDownloadLine /> Download
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div id="booking-details">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="row">
                    {/* Status Header */}
                    <div className="col-12">
                      <div
                        className={`alert alert-${
                          booking.bookingStatus === "CONFIRMED"
                            ? "success"
                            : "warning"
                        } border-0 shadow-sm mb-4`}
                      >
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-white rounded-circle p-3">
                                <RiCheckboxCircleLine
                                  className={`fs-2 ${
                                    booking.bookingStatus === "CONFIRMED"
                                      ? "text-success"
                                      : "text-warning"
                                  }`}
                                />
                              </div>
                              <div>
                                <h4
                                  className={`mb-1 ${
                                    booking.bookingStatus === "CONFIRMED"
                                      ? "text-success"
                                      : "text-warning"
                                  }`}
                                >
                                  {booking.bookingStatus === "CONFIRMED"
                                    ? "Booking Confirmed"
                                    : "Booking " + booking.bookingStatus}
                                </h4>
                                <p className="text-muted mb-0">
                                  Booking reference: {booking.bookingNo}
                                </p>
                                <p className="text-muted mb-0">
                                  PNR: {booking.pnr || "N/A"}
                                </p>
                                {booking.supplierBookingId && (
                                  <p className="text-muted mb-0">
                                    Supplier ID: {booking.supplierBookingId}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Left Column - Journey & Passenger Details */}
                    <div className="col-lg-8">
                      {/* Journey Overview Card */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0 pb-0">
                          <h4 className="mb-3 d-flex align-items-center gap-2">
                            <RiBusLine className="text-primary" />
                            Journey Overview
                          </h4>
                        </div>
                        <div className="card-body">
                          <div className="row align-items-center mb-4">
                            <div className="col-md-5 text-center">
                              <div className="bg-light rounded p-3">
                                <h5 className="text-primary mb-1">
                                  {booking.sourceCity}
                                </h5>
                                <p className="text-muted mb-0">
                                  {formatTime(booking.departureDate)}
                                </p>
                                <small className="text-muted">
                                  <RiMapPinLine className="me-1" />
                                  {booking.boardingPoint?.split(",")[0] ||
                                    booking.boardingPoint}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-2 text-center">
                              <RiArrowLeftRightLine className="text-muted fs-2" />
                              <div className="bg-primary text-white rounded-pill px-3 py-1 small">
                                Direct Journey
                              </div>
                            </div>
                            <div className="col-md-5 text-center">
                              <div className="bg-light rounded p-3">
                                <h5 className="text-success mb-1">
                                  {booking.destinationCity}
                                </h5>
                                <p className="text-muted mb-0">
                                  {booking.arrivalTime
                                    ? formatTime(booking.arrivalTime)
                                    : formatTime(booking.departureDate)}
                                </p>
                                <small className="text-muted">
                                  <RiMapPinLine className="me-1" />
                                  {booking.droppingPoint?.split(",")[0] ||
                                    booking.droppingPoint}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiCalendarLine className="text-muted" />
                                <div>
                                  <small className="text-muted">
                                    Travel Date
                                  </small>
                                  <p className="mb-0 fw-semibold">
                                    {formatDate(booking.departureDate)}
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiUserLine className="text-muted" />
                                <div>
                                  <small className="text-muted">
                                    Passengers
                                  </small>
                                  <p className="mb-0 fw-semibold">
                                    {booking.noOfPassengers ||
                                      passengerData.length ||
                                      1}{" "}
                                    Passenger(s)
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiFileList3Line className="text-muted" />
                                <div>
                                  <small className="text-muted">
                                    Booking Status
                                  </small>
                                  <p className="mb-0 fw-semibold">
                                    <span
                                      className={`badge ${
                                        booking.bookingStatus === "CONFIRMED"
                                          ? "bg-success"
                                          : booking.bookingStatus === "PENDING"
                                          ? "bg-warning"
                                          : "bg-danger"
                                      }`}
                                    >
                                      {booking.bookingStatus}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <RiCalendarLine className="text-muted" />
                                <div>
                                  <small className="text-muted">
                                    Booking Date
                                  </small>
                                  <p className="mb-0 fw-semibold">
                                    {formatDateTime(booking.bookingDate)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Boarding & Dropping Points */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0">
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <RiMapPinLine className="text-primary" />
                            Pickup & Drop Points
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="border-start border-3 border-primary ps-3">
                                <h6 className="text-primary mb-2">
                                  Boarding Point
                                </h6>
                                <p className="fw-semibold mb-1">
                                  {booking.boardingPoint}
                                </p>
                                <p className="text-muted small mb-0">
                                  Report 30 minutes before departure
                                </p>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="border-start border-3 border-success ps-3">
                                <h6 className="text-success mb-2">
                                  Dropping Point
                                </h6>
                                <p className="fw-semibold mb-1">
                                  {booking.droppingPoint}
                                </p>
                                <p className="text-muted small mb-0">
                                  Estimated arrival time
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Passenger Details */}
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom-0">
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <RiUserLine className="text-success" />
                            Passenger Details
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Sr. No</th>
                                  <th>Passenger Name</th>
                                  <th>Seat No</th>
                                  <th>Bus Fare</th>
                                  <th>Conv. Fee</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {passengerData.length > 0 ? (
                                  passengerData.map((passenger, index) => {
                                    const seatInfo = seatData[index] || {};
                                    const baseFare =
                                      passenger.Fare ||
                                      passenger.TotalFareWithTaxes ||
                                      seatInfo.BusTotal ||
                                      0;
                                    const convFee =
                                      seatInfo.ConvenienceFee || 100;
                                    const total = baseFare + convFee;

                                    return (
                                      <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>
                                          <strong>
                                            {passenger.Title} {passenger.Name}
                                          </strong>
                                          <div className="small text-muted">
                                            {passenger.Mobile} •{" "}
                                            {passenger.Email}
                                          </div>
                                        </td>
                                        <td>
                                          <span className="badge bg-primary fs-6">
                                            {passenger.SeatNbr ||
                                              seatInfo.SeatNumber ||
                                              "N/A"}
                                          </span>
                                        </td>
                                        <td>{formatCurrency(baseFare)}</td>
                                        <td>{formatCurrency(convFee)}</td>
                                        <td>
                                          <strong>
                                            {formatCurrency(total)}
                                          </strong>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td>1</td>
                                    <td>
                                      <strong>{booking.passengerName}</strong>
                                      <div className="small text-muted">
                                        {booking.contactNumber} •{" "}
                                        {booking.email}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge bg-primary fs-6">
                                        N/A
                                      </span>
                                    </td>
                                    <td>
                                      {formatCurrency(
                                        booking.baseFare ||
                                          booking.totalFare ||
                                          0
                                      )}
                                    </td>
                                    <td>
                                      {formatCurrency(
                                        booking.convenienceFee || 100
                                      )}
                                    </td>
                                    <td>
                                      <strong>
                                        {formatCurrency(
                                          booking.totalPrice ||
                                            booking.totalFare +
                                              (booking.convenienceFee || 100)
                                        )}
                                      </strong>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Fare Summary & Important Info */}
                    <div className="col-lg-4">
                      {/* Fare Summary */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0">
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <RiMoneyDollarCircleLine className="text-info" />
                            Fare Summary
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="fare-summary">
                            {/* Bus Fare */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <span className="text-muted">Bus Fare</span>
                                <div className="small text-muted">
                                  (Includes all taxes)
                                </div>
                              </div>
                              <span className="fw-semibold">
                                {formatCurrency(
                                  booking.baseFare || booking.totalFare || 0
                                )}
                              </span>
                            </div>

                            {/* Convenience Fee */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <span className="text-muted">
                                  Convenience Fee
                                </span>
                                <div className="small text-muted">
                                  INR100 per seat ×{" "}
                                  {booking.noOfPassengers || 1}
                                </div>
                              </div>
                              <span className="fw-semibold">
                                {formatCurrency(
                                  booking.convenienceFee ||
                                    (booking.noOfPassengers || 1) * 100
                                )}
                              </span>
                            </div>

                            {/* Divider */}
                            <hr className="my-3" />

                            {/* Total Amount */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <strong className="fs-5">Total Amount</strong>
                              <strong className="fs-5 text-primary">
                                {formatCurrency(
                                  booking.totalPrice ||
                                    (booking.totalFare || 0) +
                                      (booking.convenienceFee || 0)
                                )}
                              </strong>
                            </div>

                            {/* Payment Status */}
                            <div
                              className={`alert ${
                                booking.paymentStatus === "SUCCESS"
                                  ? "alert-success"
                                  : "alert-warning"
                              } mb-0 mt-3`}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <RiCheckboxCircleLine />
                                <div>
                                  <strong className="d-block">
                                    Payment:{" "}
                                    {booking.paymentStatus || "PENDING"}
                                  </strong>
                                  <small className="d-block">
                                    Payment Date:{" "}
                                    {formatDateTime(booking.bookingDate)}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Important Information */}
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom-0">
                          <h6 className="mb-0 text-warning d-flex align-items-center gap-2">
                            <RiErrorWarningLine />
                            Important Notes
                          </h6>
                        </div>
                        <div className="card-body">
                          <ul className="list-unstyled small mb-0">
                            {/* <li className="mb-2">
                              • ₹100 convenience fee per seat is included in the
                              total
                            </li> */}
                            <li className="mb-2">
                              • Arrive at boarding point 30 minutes before
                              departure
                            </li>
                            <li className="mb-2">
                              • Carry valid government-issued ID proof
                            </li>
                            <li className="mb-2">
                              • Print/download e-ticket for verification
                            </li>
                            <li className="mb-0">
                              • Contact operator for journey-related queries
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style>{`
        .bg-pink { background-color: #f06595 !important; color: white; }
        .bg-blue { background-color: #339af0 !important; color: white; }
        
        @media print {
          .btn, 
          .d-flex.gap-2,
          .btn-outline-primary,
          .main-content > .page-content > .container-fluid > .row:first-child {
            display: none !important;
          }
          
          .card { border: 1px solid #dee2e6 !important; }
          .shadow-sm { box-shadow: none !important; }
          body { padding: 10px !important; }
          .container-fluid { padding: 0 !important; }
        }
        
        @media (max-width: 768px) {
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 15px;
          }
          
          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }
          
          .col-lg-8, .col-lg-4 {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default BookingDetails;
